import React, {useState} from 'react';
import {IMask, IMaskMixin} from 'react-imask';
import moment from 'moment';
import FloatingLabel from '../../FloatingLabel/FloatingLabel';
import styled from 'styled-components';
import {IDatePickerProps} from '../DatePicker.web';
import Metrics from '../../../../Theme/Metrics';
import defaultTheme from '../../../../Theme/defaultTheme';
import {typographyVariantStyled} from '../../../Typography/Typography.styles';

type IInputDateProps = IDatePickerProps & {
  momentFormat?: string;
};

const CUSTOM_PROPS = new Set(['momentFormat']);

const StyledInput = styled.input.withConfig({
  shouldForwardProp: prop => !CUSTOM_PROPS.has(prop),
})<{}>(() => {
  const prefixIcon = false;
  const suffixIcon = false;
  return {
    flex: 1,
    paddingBottom: 0,
    paddingTop: 0,
    margin: 0,
    paddingLeft: Metrics.horizontalScale(prefixIcon ? 48 : 24),
    paddingRight: Metrics.horizontalScale(suffixIcon ? 25 : 24),
    minHeight: Metrics.verticalScale(48),
    color: defaultTheme.white,
    fontSize: typographyVariantStyled.body2.fontSize,
    fontFamily: typographyVariantStyled.body2.fontFamily,
    letterSpacing: typographyVariantStyled.body2.letterSpacing,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    zIndex: 2,
  };
});

const filterInputProps = (customProps: Set<string>, props: Record<string, any>) => {
  const inputProps: Record<string, any> = {};
  for (const key in props) {
    if (!customProps.has(key) && key !== 'style') {
      inputProps[key] = props[key];
    }
  }
  return inputProps;
};

const MaskedInput = IMaskMixin<IMask.MaskedDateOptions>(
  ({inputRef, ...props}) => {
    const [hasFocus, setHasFocus] = useState(false);
    const inputProps = filterInputProps(CUSTOM_PROPS, props);
    return (
      <FloatingLabel
        hasFocus={hasFocus}
        hasValue={!!props.defaultValue}
        label={props.label}>
        <StyledInput
          {...inputProps}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          ref={inputRef as React.Ref<HTMLInputElement>}
        />
      </FloatingLabel>
    );
  },
);

const formatHandler = (date: Date, momentFormat?: string) => {
  const separator = momentFormat?.indexOf('/') !== -1 ? '/' : '-';
  const formattedDate = moment(date).format(momentFormat);
  if (formattedDate.length === 2 || formattedDate.length === 4) {
    return formattedDate + separator;
  }
  return formattedDate;
};

const parseHandler = (str: string, momentFormat?: string) =>
  moment(str, momentFormat).toDate();

const InputDate = (props: IInputDateProps) => {
  const {momentFormat, onChange, ...rest} = props;
  const {style, ...restWithoutStyle} = rest;

  return (
    <MaskedInput
      onChange={e => onChange?.(e.currentTarget.value)}
      {...restWithoutStyle}
      mask={Date}
      pattern={momentFormat}
      lazy={true}
      eager={true}
      min={new Date(1900, 0, 1) as any}
      max={new Date() as any}
      format={date => formatHandler(date, momentFormat)}
      parse={(str: string) => parseHandler(str, momentFormat)}
      blocks={{
        DD: {mask: IMask.MaskedRange, from: 1, to: 31},
        MM: {mask: IMask.MaskedRange, from: 1, to: 12},
        YYYY: {mask: IMask.MaskedRange, from: 1900, to: moment().year()},
      }}
    />
  );
};

export default InputDate;
